import "./TourDetails.mobile-grid-fix.css";
// frontend/src/pages/TourDetails.jsx
import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import "./TourDetails.css";
import relatedToursUtils from "../utils/relatedTours";
const { getRelatedTours } = relatedToursUtils;
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Thumbs } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/thumbs";

const SITE_ORIGIN = "https://www.kirunatours.com";

/**
 * Pretix widget (English) with v2 -> v1 fallback.
 * Pass e.g. eventUrl = "https://book.kirunatours.com/kirunatours/moose-spotting/"
 */
function PretixWidgetInline({ eventUrl }) {
  useEffect(() => {
    if (!eventUrl) return;

    const url = new URL(eventUrl);
    const origin = url.origin;

    const ensureCss = (id, href) => {
      if (document.getElementById(id)) return;
      const link = document.createElement("link");
      link.id = id;
      link.rel = "stylesheet";
      link.type = "text/css";
      link.crossOrigin = "anonymous";
      link.href = href;
      document.head.appendChild(link);
    };

    ensureCss("pretix-css-v2", new URL("widget/v2.css", eventUrl).href);

    const jsIdV2 = "pretix-js-v2-en";
    const jsIdV1 = "pretix-js-v1-en";

    if (!document.getElementById(jsIdV2) && !document.getElementById(jsIdV1)) {
      const scriptV2 = document.createElement("script");
      scriptV2.id = jsIdV2;
      scriptV2.async = true;
      scriptV2.crossOrigin = "anonymous";
      scriptV2.src = new URL("/widget/v2.en.js", origin).href;

      scriptV2.onerror = () => {
        ensureCss("pretix-css-v1", new URL("widget/v1.css", eventUrl).href);

        if (!document.getElementById(jsIdV1)) {
          const scriptV1 = document.createElement("script");
          scriptV1.id = jsIdV1;
          scriptV1.async = true;
          scriptV1.crossOrigin = "anonymous";
          scriptV1.src = new URL("/widget/v1.en.js", origin).href;
          document.body.appendChild(scriptV1);
        }
      };

      document.body.appendChild(scriptV2);
    }
  }, [eventUrl]);

  return (
    <>
      <pretix-widget event={eventUrl}></pretix-widget>

      <noscript>
        <div className="pretix-widget">
          <div className="pretix-widget-info-message">
            JavaScript is disabled. Please{" "}
            <a target="_blank" rel="noopener noreferrer" href={eventUrl}>
              open the booking page
            </a>
            .
          </div>
        </div>
      </noscript>
    </>
  );
}


// Manage the standard Rezdy calendar without affecting other providers.
function getManagedRezdyPluginUrl(tour) {
  if (tour?.external_type !== "rezdy" ||
      !tour.external_iframe_url) return null;

  let scripts = tour.external_scripts;

  if (typeof scripts === "string") {
    try {
      scripts = JSON.parse(scripts);
    } catch {
      scripts = [scripts];
    }
  }

  if (!Array.isArray(scripts) ||
      scripts.length !== 1 ||
      typeof scripts[0] !== "string") return null;

  const match = scripts[0].trim().match(
    /^<script\b[^>]*\bsrc\s*=\s*["']([^"']+)["'][^>]*>\s*<\/script\s*>$/i
  );

  if (!match) return null;

  try {
    const frame = new URL(tour.external_iframe_url);
    const script = new URL(match[1]);

    if (frame.protocol !== "https:" ||
        !/\.rezdy\.com$/i.test(frame.hostname) ||
        !frame.pathname.startsWith("/calendarWidget/")) {
      return null;
    }

    return script.href === `${frame.origin}/pluginJs`
      ? script.href
      : null;
  } catch {
    return null;
  }
}

// Initialize Rezdy only when its booking iframe has loaded.
function ManagedRezdyIframe({ src, pluginUrl }) {
  const [loaded, setLoaded] = useState(false);
  const iframeRef = useRef(null);

  useEffect(() => {
    if (!loaded) return undefined;

    const script = document.createElement("script");
    script.src = pluginUrl;
    script.async = true;
    script.dataset.ktRezdyManaged = "true";

    document.body.appendChild(script);

    return () => {
      const iframe = iframeRef.current;

      if (iframe?.iFrameResizer &&
          typeof iframe.iFrameResizer.remove === "function") {
        iframe.iFrameResizer.remove();
      }

      script.remove();
    };
  }, [loaded, pluginUrl]);

  return (
    <iframe
      ref={iframeRef}
      src={src}
      title="Booking"
      className="external-booking-iframe rezdy"
      loading="lazy"
      onLoad={() => setLoaded(true)}
      style={{
        width: "100%",
        maxWidth: 400,
        height: 600,
        border: "none"
      }}
    />
  );
}


// Use the seven existing, renamed Lordicon files.
// Legacy random names in tour data are never requested.
const TOUR_ICON_FILES = new Set([
  "duration", "groups", "included", "mountain",
  "pickup", "start", "trekking"
]);

// KT TOUR DETAIL V3 KEY FACTS: choose four distinct facts from tour data.
function selectDetailKeyFacts(facts) {
  const data = Array.isArray(facts) ? facts.filter(f => f && f.label && f.value && String(f.value).trim() !== '-') : [];
  const selected = [];
  const groups = [
    /duration|length|hours|time needed/i,
    /pickup|pick.up|meeting point|transfer/i,
    /activity|experience|type of tour/i,
    /group|guest|participant|party size/i,
  ];
  for (const pattern of groups) {
    const found = data.find(f => pattern.test(String(f.label)) && !selected.includes(f));
    if (found) selected.push(found);
  }
  for (const item of data) {
    if (selected.length >= 4) break;
    if (!selected.includes(item)) selected.push(item);
  }
  return selected;
}

function resolveTourFactIcon(fact) {
  const label = String(fact?.label || "")
    .trim().toLowerCase();
  const current = String(fact?.lordIcon || "").trim();

  const named = current.match(
    /^\/lordicons\/([a-z-]+)\.json(?:[?#].*)?$/i
  );

  if (named && TOUR_ICON_FILES.has(named[1].toLowerCase())) {
    return `/lordicons/${named[1].toLowerCase()}.json`;
  }

  if (/duration|length|hours/.test(label))
    return "/lordicons/duration.json";

  if (/pickup|pick-up|meeting point|transfer/.test(label))
    return "/lordicons/pickup.json";

  if (/start|departure|meeting time/.test(label))
    return "/lordicons/start.json";

  if (/group|guest|participant|capacity|max|age/.test(label))
    return "/lordicons/groups.json";

  if (/included|inclusion|guide|equipment/.test(label))
    return "/lordicons/included.json";

  if (/snowshoe|trek|hike|hiking|walking/.test(label))
    return "/lordicons/trekking.json";

  if (/mountain|wilderness|nature|experience/.test(label))
    return "/lordicons/mountain.json";

  // Preserve existing emoji fallback for unknown fact types.
  return fact?.icon ? null :
    current ? "/lordicons/mountain.json" : null;
}

export default function TourDetails() {
  const { slug } = useParams();

  // undefined = loading, null = not found, object = loaded.
  // On a direct prerendered tour request we already have the same public
  // tour data in HTML, so React can render immediately without a
  // Loading -> content transition.
  const [tour, setTour] = useState(() => {
    if (typeof window === "undefined") return undefined;

    const initialTour = window.__KT_INITIAL_TOUR__;

    return initialTour?.slug === slug
      ? initialTour
      : undefined;
  });
  const [thumbsSwiper, setThumbsSwiper] = useState(null);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [mobileBookingOpen, setMobileBookingOpen] = useState(false);
  const mobileBookingCloseRef = useRef(null);
  const mobileBookingPanelRef = useRef(null);
  // KT_MOBILE_TOUCH_FALLBACK_V4: Samsung touch may omit the subsequent click.
  const mobileTouchFallbackTimerRef = useRef(null);
  const [similarTours, setSimilarTours] = useState([]);
  const bookingRef = useRef(null);

  // KT_MOBILE_BOOKING_POPOVER_V3: never move or remount the live booking widget.
  // Native Popover places the same DOM node in the browser top layer.
  useEffect(() => {
    if (!mobileBookingOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    mobileBookingCloseRef.current?.focus({ preventScroll: true });
    return () => { document.body.style.overflow = previousOverflow; };
  }, [mobileBookingOpen]);

  useEffect(() => {
    // Ensure no delayed touch action fires after navigating away.
    return () => {
      if (mobileTouchFallbackTimerRef.current !== null) {
        clearTimeout(mobileTouchFallbackTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    // Restore the original page state on route change/unmount.
    return () => {
      const panel = mobileBookingPanelRef.current;
      if (panel && typeof panel.hidePopover === "function" && panel.matches(":popover-open")) {
        panel.hidePopover();
      }
    };
  }, []);

  const stripHTML = (html = "") => html.replace(/<[^>]*>/g, "");
  const collapseWS = (s = "") => s.replace(/\s+/g, " ").trim();
  const smartTruncate = (s = "", max = 155) => {
    const str = (s || "").trim();
    if (!str) return "";
    if (str.length <= max) return str;
    return str.slice(0, max).replace(/\s+\S*$/, "");
  };

  const abs = (u) => {
    if (!u) return `${SITE_ORIGIN}/default-social.jpg`;
    return u.startsWith("http")
      ? u
      : `${SITE_ORIGIN}${u.startsWith("/") ? u : `/${u}`}`;
  };

  const parseJsonArray = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value;

    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  const canonicalUrl = `${SITE_ORIGIN}/tours/${slug}/`;

  useEffect(() => {
    const script = document.createElement("script");
    script.src = "/lordicon.js";
    script.defer = true;
    document.body.appendChild(script);

    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const initialTour =
      typeof window !== "undefined"
        ? window.__KT_INITIAL_TOUR__
        : undefined;

    // Keep the correct tour visible during client-side navigation.
    // Reuse embedded initial data when navigating back to the original
    // prerendered tour; otherwise show loading until the new tour arrives.
    setTour((currentTour) => {
      if (currentTour?.slug === slug) return currentTour;
      if (initialTour?.slug === slug) return initialTour;
      return undefined;
    });

    fetch(`/api/tours/${slug}`)
      .then((res) => {
        if (!res.ok) throw new Error("Tour not found");
        return res.json();
      })
      .then((data) => {
        if (isMounted) {
          setTour(data);
        }
      })
      .catch((err) => {
        console.error("Failed to load tour:", err);

        if (isMounted) {
          // A temporary API error must not remove valid server-rendered data.
          setTour((currentTour) =>
            currentTour?.slug === slug ? currentTour : null
          );
        }
      });

    return () => {
      isMounted = false;
    };
  }, [slug]);

  useEffect(() => {
    if (!tour?.is_external || !tour?.external_scripts) return;
    if (tour?.external_type === "pretix") return;
    if (getManagedRezdyPluginUrl(tour)) return;

    let scripts = [];
    if (Array.isArray(tour.external_scripts)) {
      scripts = tour.external_scripts;
    } else if (typeof tour.external_scripts === "string") {
      try {
        scripts = JSON.parse(tour.external_scripts);
      } catch {
        scripts = tour.external_scripts
          .split("\n")
          .map((s) => s.trim())
          .filter(Boolean);
      }
    }

    const insertedScripts = [];

    const insertScript = (scriptTag) => {
      const wrapper = document.createElement("div");
      wrapper.innerHTML = scriptTag;
      const script = wrapper.querySelector("script");
      if (!script) return;

      const s = document.createElement("script");
      if (script.src) {
        s.src = script.src.startsWith("http") ? script.src : "https:" + script.src;
        s.async = true;
        s.defer = true;
      } else {
        s.textContent = script.textContent;
      }
      document.body.appendChild(s);
      insertedScripts.push(s);
    };

    const isCheckfront = tour.external_type === "checkfront";
    if (isCheckfront) {
      const interfaceScript = scripts.find((s) => s.includes("interface--0.js"));
      const widgetScript = scripts.find((s) => s.includes("new DROPLET.Widget"));

      if (interfaceScript) insertScript(interfaceScript);
      const timeout = setTimeout(() => {
        if (widgetScript) insertScript(widgetScript);
      }, 500);

      return () => {
        clearTimeout(timeout);
        insertedScripts.forEach((s) => s.parentNode?.removeChild(s));
      };
    }

    scripts.forEach(insertScript);

    return () => {
      insertedScripts.forEach((s) => s.parentNode?.removeChild(s));
    };
  }, [tour?.is_external, tour?.external_type, tour?.external_scripts, tour?.external_iframe_url]);

  useEffect(() => {
    if (!tour || !tour.id) return;

    fetch("/api/tours")
      .then((res) => res.json())
      .then((data) => {
        const list = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
          setSimilarTours(getRelatedTours(tour, list, 4));
      })
      .catch((err) => console.error("Failed to load similar tours", err));
  }, [tour?.id]);

  useEffect(() => {
    try {
      if (!tour?.slug) return;
      const qs = new URLSearchParams({
        tour_slug: tour.slug,
        source: "kirunatours",
      }).toString();

      fetch(`/api/track?${qs}`, {
        method: "GET",
        cache: "no-store",
      }).catch(() => {});
    } catch {
      // tyst
    }
  }, [tour?.slug]);

  const iconMap = {
    pickup: "🚐",
    "warm clothing": "🧥",
    overall: "🧥",
    guide: "👨‍🏫",
    fika: "🍰",
    lunch: "🍲",
    transfer: "🚖",
    reindeer: "🦌",
    husky: "🐕‍🦺",
    snowmobile: "🛷",
    scooter: "🛷",
    sauna: "🧖",
    helmet: "⛑️",
    small: "👨‍👩‍👧‍👦",
    boots: "🥾",
    coffee: "☕",
    camera: "📷",
    "max persons": "👥",
    "minimum age": "🔞",
    included: "✅",
    children: "👦",
    "not included": "❌",
    "drop off": "🚐",
    instruction: "👨‍🏫",
    briefing: "👨‍🏫",
    equipment: "⛷️",
    "hot beverages": "☕",
    storytelling: "📖",
    "photo stops": "📸",
    "safety equipment": "🧤",
    "solo travelers": "🧍",
    "uneven groups": "👥",
    "no experience": "✅",
    training: "🎓",
    "weather conditions": "🌨️",
    "arrive early": "⏱️",
    "two people": "👫",
    sharing: "👫",
    team: "🐕‍🦺",
    time: "⏰",
    duration: "⏰",
    "no license": "✅",
    license: "🚫",
    "northern lights": "🌌",
    aurora: "🌌",
    guarantee: "🤞",
    "not guaranteed": "🤞",
    blankets: "🧣",
    snacks: "🍫",
    sleigh: "🛷",
    "sleigh ride": "🛷",
    purchase: "💰",
    "extra cost": "💰",
    hats: "🧤",
    gloves: "🧤",
    accessories: "🧤",
    schedule: "⏰",
    museum: "🏛️",
    ticket: "🎫",
    shop: "🛍️",
    interaction: "🤝",
    "craft shop": "🛍️",
    feeding: "🌿",
    "optional tour": "🔍",
    temperature: "❄️",
    cold: "❄️",
    hotel: "🏨",
    icehotel: "🏨",
    rooms: "🛏️",
    suites: "🛏️",
    church: "⛪",
    drinks: "🍷",
    bar: "🍷",
    rent: "💳",
    rental: "💳",
    season: "🗓️",
    "year-round": "🗓️",
    "art suites": "🎨",
    snowshoes: "⛸️",
    hat: "🧢",
    pants: "👖",
    jacket: "🧥",
    backpack: "🎒",
    hiking: "🥾",
    "extra layers": "🔄",
    change: "🔄",
    mittens: "🧤",
    sweater: "🧶",
    "child seats": "🚼",
    "extra fee": "💵",
    price: "💵",
    roads: "🛣️",
    safety: "🛡️",
    refund: "💸",
    "no refunds": "💸",
    rebooking: "🔁",
    discount: "💲",
    "viewing spots": "🎯",
    locations: "📍",
    science: "🔬",
    history: "📚",
    photography: "📷",
    "photo guidance": "📸",
    "group size": "👥",
    participants: "👥",
    "activity level": "⚡",
    easy: "✅",
    "suitable for all ages": "👨‍👩‍👧‍👦",
    "wildlife sightings": "🐾",
    facilities: "🏪",
    heated: "🔥",
    "indoor toilet": "🚻",
    camp: "🏕️",
    beginners: "🟢",
    recommended: "⭐",
    poles: "🎿",
    "hot chocolate": "☕",
    marshmallows: "🍡",
    roasting: "🔥",
    campfire: "🔥",
    traditional: "🏺",
    arctic: "❄️",
    "ice fishing": "🎣",
    "fishing gear": "🎣",
    rods: "🎣",
    bait: "🪱",
    "ice drill": "🕳️",
    "fishing license": "📄",
    fiskekort: "📄",
    "warm overalls": "🧥",
    "fishing guide": "👨‍🏫",
    "hot drinks": "☕",
    "camp access": "🚪",
    "fishing area": "🎣",
    river: "🌊",
    "direct access": "➡️",
    location: "📍",
    scenic: "🏞️",
    "scenic drive": "🚗🏞️",
    drive: "🚗",
    valley: "🏞️",
  };

  const renderWithIcons = (html) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html || "", "text/html");
    const list = Array.from(doc.querySelectorAll("li")).map((li, i) => {
      const text = li.textContent || "";
      const match = Object.entries(iconMap).find(([key]) =>
        text.toLowerCase().includes(key)
      );
      const icon = match ? match[1] : "•";
      return (
        <li key={i}>
          {icon} {text}
        </li>
      );
    });
    return <ul className="info-list">{list}</ul>;
  };

  if (tour === undefined) return <p>Loading...</p>;

  if (tour === null) {
    return (
      <div style={{ padding: "2rem", textAlign: "center" }}>
        Tour not found or failed to load.
      </div>
    );
  }

  const isAbisko = tour.locations?.includes("Abisko");
  const brandName = isAbisko ? "Abisko Guesthouse" : "Aurora Rivercamp";
  const scrollToBooking = () =>
    bookingRef.current?.scrollIntoView({ behavior: "smooth" });

  // Cancel a scheduled synthetic-free fallback if native click arrives.
  const cancelMobileTouchFallback = () => {
    if (mobileTouchFallbackTimerRef.current !== null) {
      clearTimeout(mobileTouchFallbackTimerRef.current);
      mobileTouchFallbackTimerRef.current = null;
    }
  };

  const onMobileBookingPointerUp = (event) => {
    if (event.pointerType !== "touch") return;
    cancelMobileTouchFallback();
    // The browser gets 120 ms to produce its normal click. If not, open
    // through the same React handler. No synthetic click and no double event.
    mobileTouchFallbackTimerRef.current = setTimeout(() => {
      mobileTouchFallbackTimerRef.current = null;
      openMobileBooking();
    }, 120);
  };

  const onMobileBookingClick = () => {
    cancelMobileTouchFallback();
    openMobileBooking();
  };

  const closeMobileBooking = () => {
    const panel = mobileBookingPanelRef.current;
    if (panel?.matches(":popover-open")) panel.hidePopover();
    panel?.removeAttribute("popover");
    setMobileBookingOpen(false);
  };

  const openMobileBooking = () => {
    const panel = mobileBookingPanelRef.current;
    if (!panel) return;
    // The same original Rezdy/Bókun/Checkfront instance stays mounted.
    if (typeof panel.showPopover === "function") {
      panel.setAttribute("popover", "manual");
      try {
        panel.showPopover();
      } catch (error) {
        panel.removeAttribute("popover");
        console.error("Mobile booking popover failed:", error);
      }
    }
    // CSS fixed-position fallback for browsers without Popover API.
    setMobileBookingOpen(true);
  };

  const baseDesc =
    tour.seo_description || collapseWS(tour.short_description || stripHTML(tour.description));

  const metaDesc = smartTruncate(baseDesc, 155);
  const primaryImage = abs(tour.image_url || tour.gallery?.[0]);
  const ogImage = primaryImage;

  const priceNumber = Number(tour.price || 0);
  const priceDisplay = new Intl.NumberFormat("sv-SE").format(priceNumber);

  const heroFacts = parseJsonArray(tour.hero_facts_json);
  // STEP 23C PREMIUM REACT HELPERS
  const displayLocations =
    Array.isArray(tour.locations)
      ? tour.locations
      : parseJsonArray(tour.locations);

  const displayLocation =
    displayLocations[0] ||
    (isAbisko ? "Abisko" : "Kiruna");

  const displayTitleBase =
    collapseWS(tour.title || "")
      .split("|")[0]
      .trim();

  const explicitPlacePattern =
    /kiruna|abisko|jukkasj[äa]rvi|nikkaluokta|narvik|svappavaara/i;

  const titleAlreadyHasPlace =
    explicitPlacePattern.test(displayTitleBase);

  const titleAndSlug =
    `${displayTitleBase} ${tour.slug || ""}`.toLowerCase();

  const titleDestination =
    /jukkasj[äa]rvi|kiruna|nikkaluokta|svappavaara/i.test(titleAndSlug)
      ? "Kiruna"
      : /abisko/i.test(titleAndSlug)
        ? "Abisko"
        : /narvik/i.test(titleAndSlug)
          ? "Narvik"
          : displayLocation;

  const generatedDisplayTourTitle =
    titleAlreadyHasPlace || !titleDestination
      ? displayTitleBase
      : `${displayTitleBase} in ${titleDestination}`;

  // STEP 25 DISPLAY FINISH
  const displayTitleOverrides = {
    "kiruna-arctic-tasting-experience-fire":
      "Kiruna: Arctic Tasting Experience by the Fire",
  };

  const displayTourTitle =
    displayTitleOverrides[tour.slug] ||
    generatedDisplayTourTitle;

  const displayDestination =
    titleDestination || displayLocation;

  const displayCategories =
    Array.isArray(tour.categories)
      ? tour.categories
      : parseJsonArray(tour.categories);

  const displayHeroCategory =
    displayCategories.find((category) =>
      /northern lights|dog|husky|snowmobile|reindeer|snowshoe|sami|sámi|bbq|dining|food/i.test(
        String(category)
      )
    ) ||
    tour.tour_type ||
    "Arctic Experience";

  const premiumGallery =
    Array.from(
      new Set(
        [
          tour.image_url,
          ...(Array.isArray(tour.gallery)
            ? tour.gallery
            : parseJsonArray(tour.gallery)),
        ].filter(Boolean)
      )
    );

  const premiumPickupText =
    displayLocations
      .map((location) => String(location || "").trim())
      .filter(Boolean)
      .join(", ");

  const premiumExtraFact =
    heroFacts.find((fact) => {
      const label =
        String(fact?.label || "").toLowerCase();

      return (
        fact?.value &&
        /group|max|guest|participant|start|age/.test(label) &&
        !/duration|pickup/.test(label)
      );
    });

  const premiumExtraFactText =
    premiumExtraFact
      ? `${premiumExtraFact.label}: ${premiumExtraFact.value}`
      : "";



  const itineraryItems = parseJsonArray(tour.itinerary_json);
  const parsedFaqs = parseJsonArray(tour.faq_json).filter(
    (faq) => faq?.question && faq?.answer
  );
  const parsedRelatedGuides = parseJsonArray(tour.related_guides_json).filter(
    (guide) => guide?.slug && guide?.label
  );

  const faqSchema = null;

  const jsonLdData = {
    "@context": "https://schema.org",
    "@type": ["Product", "TouristTrip"],
    name: tour.title,
    description:
      tour.short_description ||
      tour.description ||
      `Experience ${tour.title} in Kiruna and Abisko, Swedish Lapland`,
    image: ogImage,
    url: canonicalUrl,
    ...(Number.isFinite(priceNumber) && priceNumber > 0
      ? {
          offers: {
            "@type": "Offer",
            price: String(priceNumber),
            priceCurrency: "SEK",
            url: canonicalUrl,
          },
        }
      : {}),
    additionalProperty: [
      ...(Number.isFinite(Number(tour.duration)) && Number(tour.duration) > 0
        ? [{
            "@type": "PropertyValue",
            name: "duration",
            value: `PT${Number(tour.duration)}H`,
          }]
        : []),
      {
        "@type": "PropertyValue",
        name: "tourType",
        value: tour.tour_type || "Arctic Experience",
      },
      {
        "@type": "PropertyValue",
        name: "location",
        value: "Kiruna and Abisko, Swedish Lapland",
      },
      {
        "@type": "PropertyValue",
        name: "destinations",
        value: "Kiruna, Abisko",
      },
    ],
    brand: { "@type": "Brand", name: brandName },
    keywords:
      (tour.seo_keywords ? `${tour.seo_keywords}, ` : "") +
      "Kiruna, Abisko, Swedish Lapland, Norrbotten",
  };

  // KT_MOBILE_BOOKING_PORTAL_V2: exactly one booking widget.
  // When open, render it at document.body to escape the booking card's stacking context.
  const bookingWidget = (
      <div ref={mobileBookingPanelRef} className={`tour-booking-wrapper${getManagedRezdyPluginUrl(tour) ? " kt-rezdy-mobile-direct" : ""}${mobileBookingOpen ? " kt-mobile-booking-overlay" : ""}`} role={mobileBookingOpen ? "dialog" : undefined} aria-modal={mobileBookingOpen ? "true" : undefined} aria-label={mobileBookingOpen ? "Book this experience" : undefined}>
        {mobileBookingOpen && (
          <div className="kt-mobile-booking-modal-header">
            <strong>Check availability &amp; book</strong>
            <button type="button" ref={mobileBookingCloseRef} className="kt-mobile-booking-close" aria-label="Close booking" onClick={closeMobileBooking}>×</button>
          </div>
        )}
        {/* KT REZDY MOBILE DIRECT V7: use existing tour-specific URL including agentCode. */}
        {getManagedRezdyPluginUrl(tour) && (
          <div className="kt-rezdy-mobile-direct-cta">
            <a
              className="kt-rezdy-mobile-direct-link"
              href={tour.external_iframe_url}
            >
              Check dates &amp; book securely
            </a>
            {/* kt-rezdy-return-hint-v8 */}
            <p className="kt-rezdy-return-hint">You'll continue to our secure booking partner Rezdy. Use your browser's back button to return to Kiruna Tours.</p>
          </div>
        )}
        <div className="booking-background">
          <h2>Check availability & book</h2>
          <div id="kt-mobile-booking-target" ref={bookingRef} className="tour-booking-form">
            {tour.is_external && tour.external_type === "pretix" && tour.external_iframe_url ? (
              <div style={{ maxWidth: 420, margin: "0 auto" }}>
                <PretixWidgetInline eventUrl={tour.external_iframe_url} />
              </div>
            ) : tour.is_external ? (
              <div className="external-booking-wrapper" style={{ maxWidth: 400, margin: "0 auto" }}>
                {tour.external_type === "checkfront" && <div id="CHECKFRONT_WIDGET_01"></div>}

                {["rezdy", "bokun", "getyourguide"].includes(tour.external_type) &&
                  tour.external_iframe_url && (
                    getManagedRezdyPluginUrl(tour) ? (
                      <ManagedRezdyIframe
                        key={tour.external_iframe_url}
                        src={tour.external_iframe_url}
                        pluginUrl={getManagedRezdyPluginUrl(tour)}
                      />
                    ) : tour.external_type === "bokun" ? (
                        <div
                          className="bokunWidget"
                          data-src={tour.external_iframe_url}
                          style={{ width: "100%", minHeight: 600 }}
                        />
                      ) : (
                      <iframe
                      src={tour.external_iframe_url}
                      title="Booking"
                      className="external-booking-iframe"
                      loading="lazy"
                      style={{ width: "100%", maxWidth: 400, height: 600, border: "none" }}
                    />
                    )
                  )}

                {tour.external_type === "custom" && tour.external_scripts && (
                  <div dangerouslySetInnerHTML={{ __html: tour.external_scripts }} />
                )}
              </div>
            ) : (
              <div style={{ maxWidth: 400, margin: "0 auto" }}>
                <iframe
                  src={`${SITE_ORIGIN}/tours/${tour.slug}/`}
                  title="Booking form"
                  loading="lazy"
                  width="100%"
                  height="850"
                  frameBorder="0"
                  style={{ maxWidth: 400 }}
                />
              </div>
            )}
          </div>
        </div>

      </div>
  );

  return (
    <main className="tour-details-wrapper">
      <Helmet key={canonicalUrl}>
        <title>{tour.seo_title || `${tour.title} | ${brandName}`}</title>
        <meta name="description" content={metaDesc} />
        {tour.seo_keywords && <meta name="keywords" content={tour.seo_keywords} />}
        {tour.noindex ? <meta name="robots" content="noindex,nofollow" /> : null}

        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={tour.seo_title || `${tour.title} | ${brandName}`} />
        <meta property="og:description" content={metaDesc} />
        <meta property="og:image" content={ogImage} />
        <meta name="twitter:card" content="summary_large_image" />

        <script type="application/ld+json">{JSON.stringify(jsonLdData)}</script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_ORIGIN}/` },
              { "@type": "ListItem", position: 2, name: "Tours", item: `${SITE_ORIGIN}/tours/` },
              { "@type": "ListItem", position: 3, name: tour.title, item: canonicalUrl },
            ],
          })}
        </script>

        {faqSchema && (
          <script type="application/ld+json">
            {JSON.stringify(faqSchema)}
          </script>
        )}
      </Helmet>

      <header className="kt-detail-topnav" aria-label="Site navigation">
        <a className="kt-detail-brand" href="/" aria-label="Kiruna Tours home"><span aria-hidden="true">▲</span> KIRUNA TOURS</a>
        <nav aria-label="Main navigation">
          <a href="/">Home</a><a href="/tours/" aria-current="page">Tours</a>
          <a href="/travel-guide/">Travel Guide</a><a href="/about/">About</a><a href="/contact/">Contact</a>
        </nav>
        <a className="kt-detail-viewall" href="/tours/">View all tours</a>
      </header>

      {/* STEP 23C PREMIUM REACT HERO */}
      <section className="tour-premium-hero">

        <nav
          className="tour-premium-breadcrumb"
          aria-label="Breadcrumb"
        >
          <a href="/">Home</a>
          <span>›</span>
          <a href="/tours/">Tours</a>
          <span>›</span>
          <span>{displayTourTitle}</span>
        </nav>

        <div className="tour-premium-hero-grid">

          <div className="tour-premium-hero-main">

            <div className="tour-premium-eyebrow">
              {displayHeroCategory} · {displayDestination}
            </div>

            <h1>{displayTourTitle}</h1>

            {tour.short_description && (
              <p className="tour-premium-lead">
                {stripHTML(tour.short_description)}
              </p>
            )}

            <div className="tour-premium-quickfacts">

                {tour.duration && (
                  <span>
                    ◷ {tour.duration} Hours
                  </span>
                )}

                {premiumPickupText && (
                  <span>
                    ◆ Pickup: {premiumPickupText}
                  </span>
                )}

                {premiumExtraFactText && (
                  <span>
                    ◎ {premiumExtraFactText}
                  </span>
                )}

              </div>

              <div className="tour-premium-image">
              <img
                src={primaryImage}
                alt={displayTourTitle}
                width="1600"
                height="1000"
                decoding="async"
                fetchPriority="high"
              />
            </div>

            {premiumGallery.length > 1 && (
              <div className="tour-premium-static-thumbs">

                {premiumGallery
                  .slice(0, 7)
                  .map((img, index) => (
                    <button
                      type="button"
                      key={`${img}-${index}`}
                      onClick={() =>
                        setLightboxImage(abs(img))
                      }
                      aria-label={`View ${displayTourTitle} photo ${index + 1}`}
                    >
                      <img
                        src={abs(img)}
                        alt={`${displayTourTitle} thumbnail ${index + 1}`}
                        width="160"
                        height="105"
                        loading="lazy"
                      />
                    </button>
                  ))}

              </div>
            )}

      <div className="tour-icons-banner">
        {(selectDetailKeyFacts(heroFacts.length > 0
          ? heroFacts
          : [
              {
                label: "Start",
                value: tour.start_time || "-",
                lordIcon: "/lordicons/start.json",
              },
              {
                label: "Duration",
                value: tour.duration ? `${tour.duration} Hours` : "-",
                lordIcon: "/lordicons/duration.json",
              },
              {
                label: "Guide",
                value: "Professional Guide",
                lordIcon: "/lordicons/included.json",
              },
              {
                label: "Experience",
                value: tour.tour_type || "-",
                lordIcon: "/lordicons/mountain.json",
              },
            ]
        )).map((fact, index) => (
          <div className="tour-icon-item" key={`${fact.label || "fact"}-${index}`}>
            {resolveTourFactIcon(fact) ? (
              <lord-icon
                src={resolveTourFactIcon(fact)}
                trigger="loop"
                delay={fact.delay || "2000"}
                colors="primary:#19234b,secondary:#375a92"
              />
            ) : (
              <span className="tour-emoji-icon">{fact.icon || "•"}</span>
            )}

            <div className="tour-icon-label">
              <span className="tour-icon-label-title">{fact.label}</span>
              {fact.value && (
                <span className="tour-icon-label-value">{fact.value}</span>
              )}
            </div>
          </div>
        ))}
      </div>

          {/* KT DETAIL V10: gallery facts 2x2 */}
          </div>


          <aside
            className="tour-premium-bookcard"
            data-kt-mobile-modal-open={mobileBookingOpen ? "true" : undefined}
            style={mobileBookingOpen ? { overflow: "visible", zIndex: 2147483000, position: "relative" } : undefined}
          >

            <div className="tour-premium-price">

              {priceNumber > 0 ? (
                <>
                  <strong>
                    From {priceDisplay} SEK
                  </strong>
                  <span>/person</span>
                </>
              ) : (
                <strong>
                  Check availability
                </strong>
              )}

            </div>

            <p>
              View available dates and secure your place online.
            </p>

            <button
              type="button"
              className="tour-premium-bookbtn"
              onClick={scrollToBooking}
            >
              Book now
            </button>

            <div className="tour-premium-trust">

              <span>✓ Secure online booking</span>
              <span>✓ Price shown in SEK</span>

              {tour.operator_name && (
                <span>
                  ✓ Operated by {tour.operator_name}
                </span>
              )}

            </div>

          {/* KT DETAIL TOP BOOKING V4 */}
      {bookingWidget}

      <div className="kt-detail-mobile-cta">
        <span>{priceNumber > 0 ? `From ${priceDisplay} SEK` : "Check availability"}</span>
        <button
          type="button"
          className="kt-mobile-booking-link"
          onPointerUp={onMobileBookingPointerUp}
          onPointerCancel={cancelMobileTouchFallback}
          onClick={onMobileBookingClick}
          aria-haspopup="dialog"
          aria-expanded={mobileBookingOpen}
        >
          Check availability &amp; book →
        </button>
      </div>

          </aside>

        </div>

      </section>

{Array.isArray(tour.experience_highlights_json) &&
        tour.experience_highlights_json.length > 0 && (
          <section className="experience-highlights">
            <h2>Why Travelers Choose This Experience</h2>

            <div className="experience-highlights-grid">
              {tour.experience_highlights_json.slice(0, 4).map((item, index) => (
                <div key={index} className="experience-highlight-card">
                  <span className="experience-highlight-check">✓</span>
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </section>
      )}
	  
      <div className="two-column-layout">
        <div className="left-column">
          <h2 className="tour-premium-overview-title">Experience Overview</h2>

          {tour.intro_html && (
            <div
              className="tour-description"
              dangerouslySetInnerHTML={{ __html: tour.intro_html }}
            />
          )}

          {tour.local_expertise_html && (
            <section
              className="tour-local-expertise-box"
              dangerouslySetInnerHTML={{ __html: tour.local_expertise_html }}
            />
          )}

          {itineraryItems.length > 0 && (
            <section className="tour-itinerary-section">
              <h2>Experience Itinerary</h2>

              <div className="tour-itinerary-list">
                {itineraryItems.map((item, index) => (
                  <div
                    className="tour-itinerary-item"
                    key={`${item.title || item.time || "step"}-${index}`}
                  >
                    <div className="tour-itinerary-step">{index + 1}</div>

                    <div className="tour-itinerary-content">
                      {item.time && (
                        <span className="tour-itinerary-time">{item.time}</span>
                      )}

                      {item.title && <h3>{item.title}</h3>}

                      {item.description && <p>{item.description}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* STEP 24 CONTENT FLOW */}

          {tour.what_is_included && (
            <details className="collapsible-section">
              <summary>🎒 What's included</summary>
              <div className="info-content">
                {renderWithIcons(tour.what_is_included)}
              </div>
            </details>
          )}

          {tour.good_to_know && (
            <details className="collapsible-section">
              <summary>📌 Good to know</summary>
              <div className="info-content">
                {renderWithIcons(tour.good_to_know)}
              </div>
            </details>
          )}

          {parsedFaqs.length > 0 && (
            <section className="tour-faq-section tour-faq-section--main">
              <h2>Frequently Asked Questions</h2>

              <div className="tour-faq-list">
                {parsedFaqs.map((faq, index) => (
                  <details
                    className="tour-faq-item"
                    key={`${faq.question}-${index}`}
                  >
                    <summary>{faq.question}</summary>

                    <div className="tour-faq-answer">
                      <p>{faq.answer}</p>
                    </div>
                  </details>
                ))}
              </div>
            </section>
          )}

          {tour.description && (
            <details className="collapsible-section tour-seo-details">
              <summary>Discover the Full Arctic Experience</summary>

              <div
                className="tour-description"
                dangerouslySetInnerHTML={{
                  __html: tour.description,
                }}
              />
            </details>
          )}

        </div>

        <div className="right-column">
          {tour.is_external === 1 && (tour.operator_name || tour.booking_note) && (
        <section className="tour-operator-box">
          <h2>Tour Operator & Booking</h2>

          {tour.operator_name && (
            <p>
              <strong>Operated by:</strong> {tour.operator_name}
            </p>
          )}

          <p>
            <strong>Booking handled by:</strong>{" "}
            {tour.booking_partner || "Kiruna Tours"}
          </p>

          {tour.booking_note && <p>{tour.booking_note}</p>}
        </section>
      )}
          <div className="question-box">
            <h3>Have a question?</h3>
            <p>We are available between 10:00 and 18:00 local time. Feel free to reach us out.</p>
            <div className="contact-info">
              <p>+46 (0) 70 686 67 09</p>
              <p>reception@kirunatours.com</p>
            </div>
            <a className="contact-btn" href="/contact/">
              Contact
            </a>
          </div>

          {parsedRelatedGuides.length > 0 && (
            <section className="tour-related-guides-box">
              <h2>Related Arctic Guides</h2>

              <ul className="tour-related-guides-list">
                {parsedRelatedGuides.map((guide, index) => (
                  <li key={`${guide.slug}-${index}`}>
                    <a href={`/travel-guide/${guide.slug}/`}>
                      {guide.label}
                    </a>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {similarTours.length > 0 && (
            <div
              className="similar-tours-box"
              style={{ paddingLeft: "1.5rem", paddingRight: "1.5rem", marginTop: "2rem" }}
            >
              <h2>You may also like:</h2>
              <ul className="similar-tours-list" style={{ listStyle: "none", padding: 0, margin: 0 }}>
                {similarTours.map((t) => (
                  <li key={t.id} style={{ marginBottom: "0.5rem" }}>
                    <a
                      href={`/tours/${t.slug}/`}
                      title={t.title}
                      rel="noopener"
                      style={{
                        color: "#003366",
                        textDecoration: "underline",
                        fontWeight: 500,
                      }}
                    >
                      {(t.image_url || t.hero_image || t.featured_image || t.cover_image) && (
                        <img
                          className="kt-related-image"
                          src={t.image_url || t.hero_image || t.featured_image || t.cover_image}
                          alt=""
                          loading="lazy"
                          decoding="async"
                        />
                      )}
                      <span className="kt-related-title">{t.title}</span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {Array.isArray(tour.gallery) && tour.gallery.length > 0 && (
        <div className="tour-gallery-fullwidth">
          <div className="tour-gallery-section">
            <h3>Gallery</h3>
            <Swiper
              modules={[Navigation, Thumbs]}
              thumbs={{ swiper: thumbsSwiper }}
              navigation
              className="main-swiper"
            >
              {tour.gallery.map((img, i) => (
                <SwiperSlide key={i}>
                  <img
                    className="swiper-slide-img"
                    src={abs(img)}
                    alt={`${tour.title} photo ${i + 1}`}
                    width="1600"
                    height="1200"
                    decoding="async"
                    loading="lazy"
                    fetchPriority="low"
                    onClick={() => setLightboxImage(abs(img))}
                    style={{ cursor: "pointer" }}
                  />
                </SwiperSlide>
              ))}
            </Swiper>

            <Swiper
              modules={[Thumbs]}
              watchSlidesProgress
              onSwiper={setThumbsSwiper}
              slidesPerView="auto"
              spaceBetween={10}
              className="thumbs-swiper"
            >
              {tour.gallery.map((img, i) => (
                <SwiperSlide key={i}>
                  <img
                    className="swiper-slide-thumb-img"
                    src={abs(img)}
                    alt={`${tour.title} thumbnail ${i + 1}`}
                    width="160"
                    height="120"
                    decoding="async"
                    loading="lazy"
                  />
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </div>
      )}

      <div id="tour-booking" className="kt-booking-anchor" aria-hidden="true" />
      {/* KT DETAIL TOP BOOKING V4: existing booking widget moved into top booking card. */}
      {lightboxImage && (
        <div className="lightbox-overlay" onClick={() => setLightboxImage(null)}>
          <img src={lightboxImage} alt="Full view" className="lightbox-image" decoding="async" />
        </div>
      )}
    </main>
  );
}